import { db, functions, getCallPiiSidecar, storage } from "@repo/ui";
import {
	collection,
	collectionGroup,
	doc,
	getDoc,
	onSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getBlob, getDownloadURL, ref } from "firebase/storage";
import {
	ArrowLeft,
	ChevronLeft,
	ChevronRight,
	FileAudio,
	FileText,
	LoaderCircle,
	Search,
	ShieldAlert,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { MarkedRecording, MarkedTranscript } from "./PiiReviewEvidence";
import {
	markReasonError,
	ReviewInput,
	reviewMarkErrors,
} from "./PiiReviewValidation";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

type QueueCase = {
	companyId: string;
	callSid: string;
	companyName: string;
	callAt?: { toDate(): Date } | null;
	status: "open" | "blocked" | "resolved";
	reason:
		| "human_review"
		| "processing_failed"
		| "low_confidence"
		| "no_decision"
		| "correction";
	blockedReason?: string | null;
	attemptId?: string | null;
};
type Suggestion = {
	type: string;
	text?: string;
	confidence?: number;
	audioStartSec?: number | null;
	audioEndSec?: number | null;
	audioMatchConfidence?: number;
	audioRequiresHumanReview?: boolean;
	audioMatchMethod?: string;
};

type ReviewEvidence = {
	variants: Array<{
		recognizer: string;
		status: "available" | "no_word_timestamps" | "missing" | "unverified";
		wordCount: number;
		timedWordCount: number;
		startSec: number | null;
		endSec: number | null;
	}>;
	recording: {
		status: "available" | "missing" | "unverified";
		url: string | null;
	};
	isPiiRedacted: boolean;
	noSpeechReview?: { available: boolean; reason: string; sourceHash?: string };
	modes?: {
		retained: { available: boolean; reason: string };
		retranscribe: { available: boolean; reason: string };
	};
};
type PiiCandidate = {
	id: string;
	status: string;
	mode?: string;
	createdAt?: { toMillis(): number };
	failureReason?: string;
	preview?: {
		formattedTranscript?: string;
		entities?: Array<{
			type: string;
			audioMatchConfidence?: number;
			audioStartSec?: number;
			audioEndSec?: number;
		}>;
		alignment?: { requiresHumanReview?: boolean };
	};
	artifacts?: { finalAudioGcsUri?: string };
};

// GCS-created previews may have no Firebase download token. Authenticated
// Storage reads enforce the existing system-admin rules without signing IAM.
async function reviewAudioUrl(uri: string): Promise<string> {
	try {
		return await getDownloadURL(ref(storage, uri));
	} catch {
		return URL.createObjectURL(await getBlob(ref(storage, uri)));
	}
}
function releaseReviewAudio(url: string | null) {
	if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
}

function recordingTime(seconds: number): string {
	return `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60)
		.toString()
		.padStart(2, "0")}`;
}

const REASONS: Record<QueueCase["reason"], string> = {
	human_review: "Review rejected or required",
	processing_failed: "Processing failed",
	low_confidence: "Low confidence",
	no_decision: "No PII decision",
	correction: "Admin correction",
};

function formatDate(value: QueueCase["callAt"]) {
	return value?.toDate ? value.toDate().toLocaleString() : "Date unavailable";
}

export function PiiReviewPage({
	candidateId,
	companyId,
	callSid,
	editing,
	onOpenCase,
	onOpenEditor,
	onBackToQueue,
}: {
	companyId?: string | null;
	callSid?: string | null;
	editing: boolean;
	candidateId?: string;
	onOpenCase: (companyId: string, callSid: string) => void;
	onOpenEditor: (
		companyId: string,
		callSid: string,
		candidateId?: string,
	) => void;
	onBackToQueue: () => void;
}) {
	const { calls } = useData();
	const [cases, setCases] = useState<QueueCase[]>([]);
	const [error, setError] = useState("");
	const [search, setSearch] = useState("");
	const [companyFilter, setCompanyFilter] = useState("all");
	const [reasonFilter, setReasonFilter] = useState("all");
	const [statusFilter, setStatusFilter] = useState("unresolved");
	const [page, setPage] = useState(1);
	const [attempts, setAttempts] = useState<
		Array<{
			id: string;
			status?: string;
			trigger?: string;
			artifacts?: Record<string, { outputUri?: string }>;
		}>
	>([]);
	const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
	const [evidence, setEvidence] = useState<{
		key: string;
		data: ReviewEvidence | null;
		error?: string;
	} | null>(null);
	const [evidenceReload, setEvidenceReload] = useState(0);
	const [playbackError, setPlaybackError] = useState(false);
	const [playedSource, setPlayedSource] = useState("");
	const [loadedCallKey, setLoadedCallKey] = useState("");
	const [callInspectionError, setCallInspectionError] = useState(false);
	const [rerunBusy, setRerunBusy] = useState(false);
	const [rerunMessage, setRerunMessage] = useState("");
	const [candidateRuns, setCandidateRuns] = useState<{
		key: string;
		items: PiiCandidate[];
	}>({ key: "", items: [] });
	const [candidateAudio, setCandidateAudio] = useState<{
		uri: string;
		url: string;
	} | null>(null);
	const [candidateAudioStatus, setCandidateAudioStatus] = useState<
		"loading" | "ready" | "error" | "missing"
	>("missing");
	const [candidateReviewed, setCandidateReviewed] = useState(false);
	const [automaticPreviewUrl, setAutomaticPreviewUrl] = useState<string | null>(
		null,
	);
	const [selectedCallData, setSelectedCallData] = useState<{
		isPiiRedacted?: boolean;
		formattedTranscript?: string;
		gcsUri?: string;
		piiProcessing?: { attemptId?: string };
	} | null>(null);
	const selected =
		cases.find(
			(item) => item.companyId === companyId && item.callSid === callSid,
		) ?? null;
	const selectedKey = `${companyId}/${callSid}`;
	const currentSelection = useRef(selectedKey);
	currentSelection.current = selectedKey;
	const call =
		(loadedCallKey === selectedKey ? selectedCallData : null) ??
		calls.find((item) => item.companyId === companyId && item.id === callSid);
	// biome-ignore lint/correctness/useExhaustiveDependencies: The explicit evidence retry counter must refetch the same call after an inspection failure.
	useEffect(() => {
		if (editing || !companyId || !callSid) {
			setSelectedCallData(null);
			return;
		}
		let active = true;
		setCallInspectionError(false);
		void getDoc(doc(db, "companies", companyId, "calls", callSid))
			.then((snapshot) => {
				if (active) {
					setSelectedCallData(snapshot.data() ?? null);
					setLoadedCallKey(selectedKey);
				}
			})
			.catch(() => {
				if (active) {
					setSelectedCallData(null);
					setLoadedCallKey(selectedKey);
					setCallInspectionError(true);
				}
			});
		return () => {
			active = false;
		};
	}, [companyId, callSid, editing, selectedKey, evidenceReload]);

	useEffect(
		() =>
			onSnapshot(
				collectionGroup(db, "piiReviewQueue"),
				(snapshot) => {
					setCases(snapshot.docs.map((item) => item.data() as QueueCase));
					setError("");
				},
				() => setError("Could not load the PII review queue."),
			),
		[],
	);
	useEffect(() => {
		if (!companyId || !callSid) {
			setAttempts([]);
			return;
		}
		return onSnapshot(
			collection(
				db,
				"companies",
				companyId,
				"calls",
				callSid,
				"piiRedactionAttempts",
			),
			(snapshot) =>
				setAttempts(
					snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
				),
			() => setAttempts([]),
		);
	}, [companyId, callSid]);
	useEffect(() => {
		if (!companyId || !callSid) {
			setSuggestions([]);
			return;
		}
		let active = true;
		void getCallPiiSidecar({ companyId, callSid })
			.then((result) => {
				const sidecar = result.data.sidecar as {
					entities?: Suggestion[];
				} | null;
				if (active)
					setSuggestions(
						Array.isArray(sidecar?.entities) ? sidecar.entities : [],
					);
			})
			.catch(() => {
				if (active) setSuggestions([]);
			});
		return () => {
			active = false;
		};
	}, [companyId, callSid]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: The explicit evidence retry counter must refetch the same call after an inspection failure.
	useEffect(() => {
		let active = true;
		setPlaybackError(false);
		if (editing || !companyId || !callSid) {
			setEvidence(null);
			return;
		}
		setEvidence({ key: selectedKey, data: null });
		const inspect = httpsCallable<
			{ companyId: string; callSid: string },
			ReviewEvidence
		>(functions, "getCallPiiReviewEvidence");
		void inspect({ companyId, callSid })
			.then((result) => {
				if (active) setEvidence({ key: selectedKey, data: result.data });
			})
			.catch(() => {
				if (active)
					setEvidence({
						key: selectedKey,
						data: null,
						error:
							"Could not verify transcription timestamps or recording availability.",
					});
			});
		return () => {
			active = false;
		};
	}, [companyId, callSid, editing, selectedKey, evidenceReload]);
	const currentEvidence = evidence?.key === selectedKey ? evidence : null;
	const sourceAudioUrl = currentEvidence?.data?.recording?.url ?? null;

	const automaticPreviewUri = attempts.find(
		(attempt) => attempt.id === selected?.attemptId,
	)?.artifacts?.["audio.reviewPreview"]?.outputUri;
	useEffect(() => {
		let active = true;
		let loadedUrl: string | null = null;
		setAutomaticPreviewUrl(null);
		if (automaticPreviewUri)
			void reviewAudioUrl(automaticPreviewUri)
				.then((url) => {
					loadedUrl = url;
					if (active) setAutomaticPreviewUrl(url);
					else releaseReviewAudio(url);
				})
				.catch(() => {
					/* Recording preview is optional; leave playback unavailable on download failure. */
				});
		return () => {
			active = false;
			releaseReviewAudio(loadedUrl);
		};
	}, [automaticPreviewUri]);

	useEffect(() => {
		setRerunMessage("");
		setRerunBusy(false);
		if (!companyId || !callSid || editing) return;
		return onSnapshot(
			collection(
				db,
				"companies",
				companyId,
				"calls",
				callSid,
				"piiReprocessCandidates",
			),
			(snapshot) => {
				setCandidateRuns({
					key: selectedKey,
					items: snapshot.docs
						.map((item) => ({ ...item.data(), id: item.id }) as PiiCandidate)
						.sort(
							(a, b) =>
								(b.createdAt?.toMillis?.() ?? 0) -
								(a.createdAt?.toMillis?.() ?? 0),
						),
				});
			},
			() => setRerunMessage("Could not load automated rerun progress."),
		);
	}, [companyId, callSid, editing, selectedKey]);
	const runs = candidateRuns.key === selectedKey ? candidateRuns.items : [];
	const running = runs.find((run) =>
		["queued", "acquiring", "processing", "promoting"].includes(run.status),
	);
	const candidate =
		running ??
		runs.find((run) => !["discarded", "promoted"].includes(run.status));
	const candidateUri = candidate?.artifacts?.finalAudioGcsUri;
	// biome-ignore lint/correctness/useExhaustiveDependencies: Candidate, call, and retry identity changes must discard old playback and review state even when the audio URI is reused.
	useEffect(() => {
		let active = true;
		let loadedUrl: string | null = null;
		setCandidateReviewed(false);
		setCandidateAudio(null);
		setCandidateAudioStatus(candidateUri ? "loading" : "missing");
		if (candidateUri)
			void reviewAudioUrl(candidateUri)
				.then((url) => {
					loadedUrl = url;
					if (active) {
						setCandidateAudio({ uri: candidateUri, url });
						setCandidateAudioStatus("ready");
					} else releaseReviewAudio(url);
				})
				.catch(() => {
					if (active) setCandidateAudioStatus("error");
				});
		return () => {
			active = false;
			releaseReviewAudio(loadedUrl);
		};
	}, [candidateUri, candidate?.id, selectedKey, evidenceReload]);

	const startRerun = async (mode: "retained_transcripts" | "retranscribe") => {
		if (!companyId || !callSid) return;
		setRerunBusy(true);
		setRerunMessage("");
		try {
			const start = httpsCallable(functions, "startCallPiiReprocess");
			await start({ companyId, callSid, mode });
			toast.success(
				"Automated PII rerun started. The stored call stays unchanged until publication.",
			);
		} catch (error) {
			if (currentSelection.current === selectedKey)
				setRerunMessage(
					error instanceof Error
						? error.message
						: "Could not start the PII rerun.",
				);
		} finally {
			if (currentSelection.current === selectedKey) setRerunBusy(false);
		}
	};
	const resolveCandidate = async (action: "promote" | "discard") => {
		if (!companyId || !callSid || !candidate) return;
		setRerunBusy(true);
		setRerunMessage("");
		try {
			const resolve = httpsCallable(functions, "resolveCallPiiReprocess");
			await resolve({ companyId, callSid, candidateId: candidate.id, action });
			setEvidenceReload((value) => value + 1);
			toast.success(
				action === "promote"
					? "Reviewed candidate published."
					: "Candidate discarded.",
			);
		} catch (error) {
			if (currentSelection.current === selectedKey)
				setRerunMessage(
					error instanceof Error
						? error.message
						: "Could not resolve the PII candidate.",
				);
		} finally {
			if (currentSelection.current === selectedKey) setRerunBusy(false);
		}
	};

	const companies = useMemo(
		() => [
			...new Map(
				cases.map((item) => [item.companyId, item.companyName]),
			).entries(),
		],
		[cases],
	);
	const filtered = useMemo(
		() =>
			cases
				.filter((item) => {
					const haystack =
						`${item.companyName} ${item.callSid} ${REASONS[item.reason]}`.toLowerCase();
					return (
						haystack.includes(search.toLowerCase()) &&
						(companyFilter === "all" || item.companyId === companyFilter) &&
						(reasonFilter === "all" || item.reason === reasonFilter) &&
						(statusFilter === "all" ||
							(statusFilter === "unresolved"
								? item.status !== "resolved"
								: item.status === statusFilter))
					);
				})
				.sort(
					(a, b) =>
						(a.callAt?.toDate?.().getTime() ?? 0) -
						(b.callAt?.toDate?.().getTime() ?? 0),
				),
		[cases, search, companyFilter, reasonFilter, statusFilter],
	);
	const pageCount = Math.max(1, Math.ceil(filtered.length / 20));
	const visible = filtered.slice(
		(Math.min(page, pageCount) - 1) * 20,
		Math.min(page, pageCount) * 20,
	);

	if (editing && companyId && callSid) {
		return (
			<PiiReviewEditor
				key={`${companyId}/${callSid}/${candidateId ?? "stored"}`}
				companyId={companyId}
				callSid={callSid}
				suggestions={candidateId ? [] : suggestions}
				candidateId={candidateId}
				onBack={() => onOpenCase(companyId, callSid)}
			/>
		);
	}

	return (
		<div className="space-y-5">
			<header className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
						Workspace / Privacy
					</p>
					<h1 className="mt-1 text-3xl font-semibold tracking-tight">
						PII review
					</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						{cases.filter((item) => item.status !== "resolved").length} open
						cases · Oldest calls first
					</p>
				</div>
			</header>
			{error && (
				<p
					role="alert"
					className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
				>
					{error}
				</p>
			)}
			<div className="grid gap-4 xl:grid-cols-[minmax(340px,0.95fr)_minmax(400px,1.3fr)]">
				<section
					aria-label="PII review queue"
					className="min-w-0 rounded-lg border bg-white"
				>
					<div className="space-y-3 border-b p-4">
						<label className="relative block">
							<Search
								className="absolute left-3 top-2.5 size-4 text-muted-foreground"
								aria-hidden="true"
							/>
							<span className="sr-only">Search company or call ID</span>
							<Input
								className="pl-9"
								value={search}
								onChange={(event) => {
									setSearch(event.target.value);
									setPage(1);
								}}
								placeholder="Search company or call ID"
							/>
						</label>
						<div className="grid gap-2 sm:grid-cols-3">
							<label className="text-xs">
								Company
								<select
									aria-label="Company"
									className="mt-1 w-full rounded-md border bg-white p-2 text-sm"
									value={companyFilter}
									onChange={(event) => {
										setCompanyFilter(event.target.value);
										setPage(1);
									}}
								>
									<option value="all">All companies</option>
									{companies.map(([id, name]) => (
										<option key={id} value={id}>
											{name}
										</option>
									))}
								</select>
							</label>
							<label className="text-xs">
								Reason
								<select
									aria-label="Reason"
									className="mt-1 w-full rounded-md border bg-white p-2 text-sm"
									value={reasonFilter}
									onChange={(event) => {
										setReasonFilter(event.target.value);
										setPage(1);
									}}
								>
									<option value="all">All reasons</option>
									{Object.entries(REASONS).map(([id, label]) => (
										<option key={id} value={id}>
											{label}
										</option>
									))}
								</select>
							</label>
							<label className="text-xs">
								Status
								<select
									aria-label="Status"
									className="mt-1 w-full rounded-md border bg-white p-2 text-sm"
									value={statusFilter}
									onChange={(event) => {
										setStatusFilter(event.target.value);
										setPage(1);
									}}
								>
									<option value="unresolved">Open and blocked</option>
									<option value="open">Open</option>
									<option value="blocked">Blocked</option>
									<option value="resolved">Resolved</option>
									<option value="all">All</option>
								</select>
							</label>
						</div>
					</div>
					<div className="max-h-[68vh] overflow-y-auto">
						{visible.length ? (
							visible.map((item) => (
								<button
									type="button"
									key={`${item.companyId}/${item.callSid}`}
									onClick={() => onOpenCase(item.companyId, item.callSid)}
									aria-current={
										item.companyId === companyId && item.callSid === callSid
											? "true"
											: undefined
									}
									className={`w-full border-b p-4 text-left transition-colors hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-teal-700 ${item.companyId === companyId && item.callSid === callSid ? "bg-teal-50" : ""}`}
								>
									<div className="flex items-center justify-between gap-2">
										<strong className="truncate text-sm">
											{item.companyName}
										</strong>
										<span
											className={`text-xs font-semibold ${item.status === "blocked" ? "text-red-700" : "text-slate-600"}`}
										>
											{item.status}
										</span>
									</div>
									<div className="mt-1 text-xs text-muted-foreground">
										{formatDate(item.callAt)} · {item.callSid}
									</div>
									<div className="mt-2 text-xs">{REASONS[item.reason]}</div>
								</button>
							))
						) : (
							<p className="p-8 text-center text-sm text-muted-foreground">
								No cases match these filters.
							</p>
						)}
					</div>
					<div className="flex items-center justify-between border-t p-3 text-xs">
						<span>
							Page {Math.min(page, pageCount)} of {pageCount}
						</span>
						<div className="flex gap-1">
							<Button
								size="sm"
								variant="outline"
								aria-label="Previous page"
								disabled={page <= 1}
								onClick={() => setPage(page - 1)}
							>
								<ChevronLeft className="size-4" />
							</Button>
							<Button
								size="sm"
								variant="outline"
								aria-label="Next page"
								disabled={page >= pageCount}
								onClick={() => setPage(page + 1)}
							>
								<ChevronRight className="size-4" />
							</Button>
						</div>
					</div>
				</section>
				<section
					aria-label="Selected PII case"
					className="min-w-0 rounded-lg border bg-white p-5"
				>
					{selected ? (
						<div className="space-y-6">
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div>
									<p className="text-xs text-muted-foreground">
										{selected.companyName} · {formatDate(selected.callAt)}
									</p>
									<h2 className="mt-1 break-all text-xl font-semibold">
										{selected.callSid}
									</h2>
									<p className="mt-1 text-sm">
										{REASONS[selected.reason]} · {selected.status}
									</p>
								</div>
								<Button variant="outline" size="sm" onClick={onBackToQueue}>
									<ArrowLeft className="mr-2 size-4" />
									Clear selection
								</Button>
							</div>
							{automaticPreviewUrl && (
								<div className="space-y-2">
									<h3 className="text-sm font-semibold">
										Automatic audio preview
									</h3>
									<p className="text-xs text-muted-foreground">
										Available matches are muted. Approximate and missing ranges
										still need review before publication.
									</p>
									<audio controls src={automaticPreviewUrl} className="w-full">
										<track kind="captions" />
									</audio>
								</div>
							)}
							<section
								aria-labelledby="inspector-transcript-heading"
								className="space-y-3"
							>
								<h3
									id="inspector-transcript-heading"
									className="flex items-center gap-2 text-sm font-semibold"
								>
									<FileText
										className="size-4 text-teal-700"
										aria-hidden="true"
									/>
									Stored transcript
								</h3>
								{loadedCallKey !== selectedKey ? (
									<EvidenceLoading label="Loading stored transcript…" />
								) : callInspectionError ? (
									<EvidenceError
										message="Could not load the stored transcript."
										onRetry={() => setEvidenceReload((value) => value + 1)}
									/>
								) : call?.formattedTranscript ? (
									<div
										role="region"
										aria-label="Stored call transcript"
										tabIndex={0}
										dir="auto"
										className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-md border p-4 text-sm leading-relaxed"
									>
										{call.formattedTranscript}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">
										No stored transcript is available.
									</p>
								)}
								<p className="text-xs text-muted-foreground">
									This is the stored display transcript. Word timestamps are
									checked separately in the retained speech-to-text files.
								</p>
								{currentEvidence?.error ? (
									<EvidenceError
										message={currentEvidence.error}
										onRetry={() => setEvidenceReload((value) => value + 1)}
									/>
								) : !currentEvidence?.data ? (
									<EvidenceLoading label="Checking recording and word timestamps…" />
								) : (
									<div className="space-y-2">
										<h4 className="text-sm font-semibold">
											Transcription timing
										</h4>
										{currentEvidence.data.variants.length ? (
											<ul className="space-y-2 text-sm">
												{currentEvidence.data.variants.map((variant, index) => (
													<li
														key={`${variant.recognizer}-${index}`}
														className="rounded-md border p-3"
													>
														<strong>{variant.recognizer}</strong>
														<p className="mt-1">
															{variant.status === "available"
																? `${variant.timedWordCount} of ${variant.wordCount} words have timestamps · ${recordingTime(variant.startSec ?? 0)}–${recordingTime(variant.endSec ?? 0)}`
																: variant.status === "no_word_timestamps"
																	? "No usable word timestamps"
																	: variant.status === "missing"
																		? "Timestamped output is missing or was deleted"
																		: "Timestamped output could not be verified"}
														</p>
													</li>
												))}
											</ul>
										) : (
											<p className="text-sm text-muted-foreground">
												No retained speech-to-text outputs were found.
											</p>
										)}
										<p className="text-xs text-muted-foreground">
											{currentEvidence.data.variants.some(
												(variant) => variant.status === "available",
											)
												? "Word timestamps are available for audio matching. Retranscribe if the text or timing is inaccurate."
												: "Audio matching needs word timestamps. Retranscribe the original recording if it is still available."}
										</p>
									</div>
								)}
							</section>
							<section
								aria-labelledby="inspector-recording-heading"
								className="space-y-3"
							>
								<h3
									id="inspector-recording-heading"
									className="flex items-center gap-2 text-sm font-semibold"
								>
									<FileAudio
										className="size-4 text-teal-700"
										aria-hidden="true"
									/>
									Stored recording
								</h3>
								{sourceAudioUrl && !playbackError ? (
									<>
										<p className="text-xs text-muted-foreground">
											{currentEvidence?.data?.isPiiRedacted
												? "PII review completed for this recording."
												: "PII review is pending. This recording may still contain sensitive information."}
										</p>
										<audio
											key={selectedKey}
											aria-label="Stored call recording"
											controls
											preload="metadata"
											src={sourceAudioUrl}
											className="w-full"
											onPlay={() =>
												setPlayedSource(
													`${selectedKey}/${currentEvidence?.data?.noSpeechReview?.sourceHash}/${evidenceReload}`,
												)
											}
											onError={() => setPlaybackError(true)}
										>
											<track kind="captions" />
										</audio>
									</>
								) : playbackError ? (
									<EvidenceError
										message="The recording could not be played. Refresh its playback link and try again."
										onRetry={() => setEvidenceReload((value) => value + 1)}
									/>
								) : currentEvidence?.data ? (
									<p className="text-sm text-muted-foreground">
										{currentEvidence.data.recording.status === "missing"
											? "The stored recording is missing."
											: "The stored recording could not be verified for playback."}
									</p>
								) : null}
							</section>

							{currentEvidence?.data?.noSpeechReview?.available &&
								currentEvidence.data.noSpeechReview.sourceHash && (
									<NoSpeechReview
										key={`${selectedKey}/${currentEvidence.data.noSpeechReview.sourceHash}/${evidenceReload}`}
										companyId={selected.companyId}
										callSid={selected.callSid}
										sourceHash={currentEvidence.data.noSpeechReview.sourceHash}
										canConfirm={
											Boolean(sourceAudioUrl) &&
											!playbackError &&
											playedSource ===
												`${selectedKey}/${currentEvidence.data.noSpeechReview.sourceHash}/${evidenceReload}`
										}
										disabled={
											rerunBusy ||
											Boolean(
												candidate &&
													!["discarded", "promoted", "failed"].includes(
														candidate.status,
													),
											)
										}
										onBusy={setRerunBusy}
										onReload={() => setEvidenceReload((value) => value + 1)}
										onResolved={() => {
											setEvidenceReload((value) => value + 1);
											onBackToQueue();
										}}
									/>
								)}

							<p className="text-xs text-muted-foreground">
								{suggestions.length} suggested PII matches available for review
							</p>
							<div>
								<h3 className="text-sm font-semibold">PII history</h3>
								<p className="mt-1 text-xs text-muted-foreground">
									Latest attempt: {selected.attemptId ?? "None"}
								</p>
								<ul className="mt-2 space-y-1 text-xs">
									{attempts.map((attempt) => (
										<li key={attempt.id} className="rounded border p-2">
											{attempt.id} · {attempt.status ?? "unknown"} ·{" "}
											{attempt.trigger ?? "automatic"}
										</li>
									))}
								</ul>
							</div>
							{selected.status === "blocked" && (
								<p className="text-sm text-red-800">
									<ShieldAlert className="mb-2 size-4" />
									{currentEvidence?.data?.noSpeechReview?.available
										? "Transcript-based review is blocked. You can review the retained recording above to make a no-speech decision."
										: `Blocked: ${selected.blockedReason ?? "Source evidence is unavailable"}.`}
								</p>
							)}
							<div className="flex flex-wrap gap-2">
								<Button
									onClick={() =>
										onOpenEditor(
											selected.companyId,
											selected.callSid,
											candidate &&
												["ready", "review_required"].includes(candidate.status)
												? candidate.id
												: undefined,
										)
									}
									disabled={
										(selected.status === "blocked" &&
											!["ready", "review_required"].includes(
												candidate?.status ?? "",
											)) ||
										rerunBusy ||
										Boolean(running)
									}
									className="bg-teal-700 text-white hover:bg-teal-800"
								>
									{candidate &&
									["ready", "review_required"].includes(candidate.status)
										? "Edit latest candidate"
										: call?.isPiiRedacted === true
											? "Reopen for correction"
											: "Open editor"}
								</Button>
								<Button
									variant="outline"
									disabled={
										rerunBusy ||
										Boolean(running) ||
										!currentEvidence?.data?.modes?.retained.available
									}
									onClick={() => void startRerun("retained_transcripts")}
								>
									Rerun automatic PII
								</Button>
								<Button
									variant="outline"
									disabled={
										rerunBusy ||
										Boolean(running) ||
										!currentEvidence?.data?.modes?.retranscribe.available
									}
									onClick={() => void startRerun("retranscribe")}
								>
									Retranscribe &amp; rerun PII
								</Button>
							</div>
							{currentEvidence?.data?.modes && (
								<dl className="space-y-2 text-xs text-muted-foreground">
									<div>
										<dt className="font-semibold">Rerun automatic PII</dt>
										<dd>{currentEvidence.data.modes.retained.reason}</dd>
									</div>
									<div>
										<dt className="font-semibold">
											Retranscribe &amp; rerun PII
										</dt>
										<dd>{currentEvidence.data.modes.retranscribe.reason}</dd>
									</div>
								</dl>
							)}
							{rerunBusy && (
								<p role="status" className="text-sm">
									Preparing the PII rerun…
								</p>
							)}
							{rerunMessage && (
								<p role="alert" className="text-sm text-red-700">
									{rerunMessage}
								</p>
							)}
							{candidate && (
								<section
									aria-label="Automated PII rerun"
									className="space-y-3 rounded-md border p-4"
								>
									<h3 className="text-sm font-semibold">Automated PII rerun</h3>
									<p role="status" className="text-sm">
										{candidate.status.replaceAll("_", " ")}
									</p>
									{candidate.failureReason && (
										<p className="text-sm text-red-700">
											{candidate.failureReason}
										</p>
									)}
									{candidate.preview?.formattedTranscript && (
										<div
											dir="auto"
											className="max-h-64 overflow-y-auto whitespace-pre-wrap text-sm"
										>
											{candidate.preview.formattedTranscript}
										</div>
									)}
									{candidateAudioStatus === "loading" && (
										<p role="status" className="text-sm">
											Loading candidate recording…
										</p>
									)}
									{candidateAudioStatus === "missing" && (
										<p className="text-sm">
											Candidate recording is not available yet.
										</p>
									)}
									{candidateAudioStatus === "error" && (
										<div role="alert" className="text-sm">
											<p>Could not play the candidate recording.</p>
											<Button
												variant="outline"
												onClick={() => setEvidenceReload((value) => value + 1)}
											>
												Retry candidate playback
											</Button>
										</div>
									)}
									{candidateAudio && candidateAudio.uri === candidateUri && (
										<audio
											controls
											onError={() => {
												setCandidateReviewed(false);
												setCandidateAudio(null);
												setCandidateAudioStatus("error");
											}}
											aria-label="Automated PII candidate recording"
											src={candidateAudio.url}
											className="w-full"
										>
											<track kind="captions" />
										</audio>
									)}
									{candidate.status === "review_required" && (
										<p className="text-sm">
											The candidate still has uncertain or missing PII evidence
											and cannot be published automatically. Choose Edit latest
											candidate to mark its original recording and transcript,
											or discard it.
										</p>
									)}
									{candidate.status === "ready" && (
										<label className="flex items-center gap-2 text-sm">
											<input
												type="checkbox"
												checked={candidateReviewed}
												disabled={
													!candidateAudio ||
													!candidate.preview?.formattedTranscript
												}
												onChange={(event) =>
													setCandidateReviewed(event.target.checked)
												}
											/>
											I reviewed the candidate transcript and listened to its
											recording
										</label>
									)}
									{["ready", "review_required", "failed"].includes(
										candidate.status,
									) && (
										<div className="flex flex-wrap gap-2">
											<Button
												variant="outline"
												disabled={rerunBusy}
												onClick={() => void resolveCandidate("discard")}
											>
												Discard candidate
											</Button>
											<Button
												disabled={
													rerunBusy ||
													candidate.status !== "ready" ||
													!candidateReviewed ||
													candidate.preview?.alignment?.requiresHumanReview ===
														true
												}
												onClick={() => void resolveCandidate("promote")}
											>
												Publish reviewed candidate
											</Button>
										</div>
									)}
								</section>
							)}
						</div>
					) : (
						<div className="flex min-h-80 items-center justify-center text-sm text-muted-foreground">
							Select a case to inspect its evidence and history.
						</div>
					)}
				</section>
			</div>
		</div>
	);
}

function NoSpeechReview({
	companyId,
	callSid,
	sourceHash,
	canConfirm,
	disabled,
	onBusy,
	onReload,
	onResolved,
}: {
	companyId: string;
	callSid: string;
	sourceHash: string;
	canConfirm: boolean;
	disabled: boolean;
	onBusy: (busy: boolean) => void;
	onReload: () => void;
	onResolved: () => void;
}) {
	const [confirmed, setConfirmed] = useState(false);
	const [reason, setReason] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const active = useRef(true);
	useEffect(() => {
		active.current = true;
		return () => {
			active.current = false;
		};
	}, []);
	const resolve = async () => {
		if (!confirmed || !canConfirm || !reason.trim() || disabled || busy) return;
		setBusy(true);
		onBusy(true);
		setError("");
		try {
			await httpsCallable(
				functions,
				"resolveCallPiiNoSpeech",
			)({
				companyId,
				callSid,
				sourceHash,
				reason: reason.trim(),
				reviewedAudio: true,
			});
			if (active.current) {
				toast.success("Review completed: no speech or PII found.");
				onResolved();
			}
		} catch (failure) {
			if (active.current)
				setError(
					failure instanceof Error
						? failure.message
						: "Could not resolve this call. Reload its evidence and try again.",
				);
		} finally {
			if (active.current) {
				setBusy(false);
				onBusy(false);
			}
		}
	};
	return (
		<section
			aria-labelledby="no-speech-review-heading"
			className="space-y-3 border-t pt-5"
		>
			<h3 id="no-speech-review-heading" className="text-sm font-semibold">
				Resolve without a transcript
			</h3>
			<p className="text-sm text-muted-foreground">
				Play the retained recording above and listen to it in full. Use this
				only when there is no speech or sensitive content. Publishing this
				decision releases the recording to the company.
			</p>
			<label className="flex items-start gap-2 text-sm">
				<input
					type="checkbox"
					checked={confirmed}
					disabled={!canConfirm || disabled || busy}
					onChange={(event) => setConfirmed(event.target.checked)}
				/>
				I listened to the entire recording and found no speech or sensitive
				content.
			</label>
			<label className="block text-sm">
				Review reason
				<Input
					value={reason}
					maxLength={1000}
					disabled={disabled || busy}
					onChange={(event) => setReason(event.target.value)}
					className="mt-1"
				/>
			</label>
			{error && (
				<div className="space-y-2">
					<p role="alert" className="text-sm text-red-700">
						{error}
					</p>
					<Button variant="outline" disabled={busy} onClick={onReload}>
						Reload review evidence
					</Button>
				</div>
			)}
			<Button
				disabled={
					!canConfirm || !confirmed || !reason.trim() || disabled || busy
				}
				onClick={() => void resolve()}
			>
				{busy ? "Publishing review…" : "Resolve: no speech / no PII found"}
			</Button>
		</section>
	);
}

function EvidenceLoading({ label }: { label: string }) {
	return (
		<div
			role="status"
			aria-busy="true"
			className="mt-4 flex min-h-28 items-center justify-center gap-2 rounded-md bg-slate-50 p-4 text-sm text-muted-foreground"
		>
			<LoaderCircle
				aria-hidden="true"
				className="size-4 animate-spin motion-reduce:animate-none"
			/>
			{label}
		</div>
	);
}

function EvidenceError({
	message,
	onRetry,
}: {
	message: string;
	onRetry?: () => void;
}) {
	return (
		<div className="mt-4 space-y-3">
			<p role="alert" className="text-sm text-red-700">
				{message}
			</p>
			{onRetry && (
				<Button variant="outline" onClick={onRetry}>
					Retry loading source
				</Button>
			)}
		</div>
	);
}

function PiiReviewEditor({
	candidateId,
	companyId,
	callSid,
	suggestions,
	onBack,
}: {
	companyId: string;
	callSid: string;
	suggestions: Suggestion[];
	candidateId?: string;
	onBack: () => void;
}) {
	type MarkType =
		| "credit_card_number"
		| "cvv"
		| "card_expiration"
		| "bank_account"
		| "national_id"
		| "authentication_phrase"
		| "other_sensitive_content";
	type TextMark = {
		start: number;
		end: number;
		type: MarkType;
		reason?: string;
	};
	type AudioMark = {
		startSec: number;
		endSec: number;
		type: MarkType;
		reason?: string;
		confidence?: number;
		requiresReview?: boolean;
		confirmed?: boolean;
	};
	const [source, setSource] = useState<{
		formattedTranscript?: string;
		gcsUri?: string;
		piiProcessing?: { attemptId?: string };
	} | null>(null);
	const [editingCandidateId, setEditingCandidateId] = useState(candidateId);
	const [sourceStatus, setSourceStatus] = useState<
		"loading" | "ready" | "error"
	>("loading");
	const [sourceError, setSourceError] = useState("");
	const [audioStatus, setAudioStatus] = useState<
		"loading" | "ready" | "error" | "missing"
	>("loading");
	const [waveformError, setWaveformError] = useState(false);
	const [loadAttempt, setLoadAttempt] = useState(0);
	const [sourceHash, setSourceHash] = useState("");
	const [sourceAudioUrl, setSourceAudioUrl] = useState<string | null>(null);
	const [textMarks, setTextMarks] = useState<TextMark[]>([]);
	const [audioMarks, setAudioMarks] = useState<AudioMark[]>([]);
	const [savedMarks, setSavedMarks] = useState("");
	const [selectedType, setSelectedType] =
		useState<MarkType>("credit_card_number");
	const [reason, setReason] = useState("");
	const [revision, setRevision] = useState(0);
	const [preview, setPreview] = useState<{
		proposedTranscript: string;
		audioUri: string;
		id: string;
		publicationBlocked?: boolean;
		publicationBlockers?: string[];
	} | null>(null);
	const [previewAudioStatus, setPreviewAudioStatus] = useState<
		"loading" | "ready" | "error"
	>("loading");
	const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
	const [reviewedText, setReviewedText] = useState(false);
	const [reviewedAudio, setReviewedAudio] = useState(false);
	const [manualOverride, setManualOverride] = useState(false);
	const [publicationIssue, setPublicationIssue] = useState<{
		message: string;
		artifact: string;
		field: string;
	} | null>(null);
	const [publishReason, setPublishReason] = useState("");
	const [draftStatus, setDraftStatus] = useState("draft");
	const [canCancelCorrection, setCanCancelCorrection] = useState(false);
	const [message, setMessage] = useState("");
	const [busy, setBusy] = useState(false);
	const [duration, setDuration] = useState(0);
	const [peaks, setPeaks] = useState<number[]>([]);
	const [rangeStart, setRangeStart] = useState(0);
	const [rangeEnd, setRangeEnd] = useState(0);
	const transcriptRef = useRef<HTMLTextAreaElement>(null);
	const audioRef = useRef<HTMLAudioElement>(null);
	const transcript = source?.formattedTranscript ?? "";
	const typeOptions: Array<[MarkType, string]> = [
		["credit_card_number", "Credit card number"],
		["cvv", "CVV"],
		["card_expiration", "Card expiration"],
		["bank_account", "Bank account"],
		["national_id", "National ID"],
		["authentication_phrase", "Authentication phrase"],
		["other_sensitive_content", "Other sensitive content"],
	];
	// biome-ignore lint/correctness/useExhaustiveDependencies: The explicit retry counter must reload the source after a failed attempt.
	useEffect(() => {
		let active = true;
		let objectUrl: string | null = null;
		setSourceStatus("loading");
		setSourceError("");
		setAudioStatus("loading");
		setSource(null);
		setSourceAudioUrl(null);
		// Reset before mounting the next player; cached metadata may arrive before
		// the waveform effect and must not be overwritten by its initialization.
		setDuration(0);
		const prepare = httpsCallable<
			unknown,
			{
				canCancel?: boolean;
				candidateId?: string;
				source?: {
					formattedTranscript: string;
					gcsUri: string;
					piiProcessing?: { attemptId?: string };
				};
			}
		>(functions, "openCallPiiCorrection");
		void prepare({
			companyId,
			callSid,
			reason: "Admin opened the PII review editor",
			...(candidateId ? { candidateId } : {}),
		})
			.then(async (result) => {
				if (active) {
					setCanCancelCorrection(result.data.canCancel === true);
					setEditingCandidateId(result.data.candidateId);
				}
				if (result.data.source) return result.data.source;
				if (candidateId)
					throw new Error(
						"The candidate source was not returned. Deploy the updated review functions before editing a candidate.",
					);
				return (
					await getDoc(doc(db, "companies", companyId, "calls", callSid))
				).data();
			})
			.then(async (data) => {
				if (!data) throw new Error("Source call not found.");
				const text =
					typeof data.formattedTranscript === "string"
						? data.formattedTranscript
						: "";
				const uri = typeof data.gcsUri === "string" ? data.gcsUri : null;
				const encoded = new TextEncoder().encode(JSON.stringify([text, uri]));
				const digest = await crypto.subtle.digest("SHA-256", encoded);
				const hash = [...new Uint8Array(digest)]
					.map((byte) => byte.toString(16).padStart(2, "0"))
					.join("");
				if (!active) return;
				setSource(data);
				setSourceHash(hash);
				setSourceStatus("ready");
				if (uri) {
					void getDownloadURL(ref(storage, uri))
						.catch(async () => {
							const blob = await getBlob(ref(storage, uri));
							if (!active) return null;
							objectUrl = URL.createObjectURL(blob);
							return objectUrl;
						})
						.then((url) => {
							if (active && url) {
								setSourceAudioUrl(url);
								setAudioStatus("ready");
							}
						})
						.catch(() => {
							if (active) setAudioStatus("error");
						});
				} else setAudioStatus("missing");
			})
			.catch((error: unknown) => {
				if (active) {
					setSourceError(error instanceof Error ? error.message : "");
					setSourceStatus("error");
					setAudioStatus("error");
				}
			});
		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [companyId, callSid, candidateId, loadAttempt]);
	useEffect(
		() =>
			onSnapshot(
				doc(
					db,
					"companies",
					companyId,
					"calls",
					callSid,
					"piiReviewDrafts",
					"current",
				),
				(snapshot) => {
					if (!snapshot.exists()) return;
					const data = snapshot.data();
					const recovering =
						data.status === "publication_failed" &&
						typeof data.reviewAttemptId === "string" &&
						source?.piiProcessing?.attemptId === data.reviewAttemptId;
					if (
						data.sourceHash !== sourceHash &&
						sourceHash &&
						!recovering &&
						!(
							canCancelCorrection &&
							["published", "cancelled"].includes(data.status)
						)
					) {
						setMessage(
							"The saved draft belongs to an older source revision. Reload the call before editing.",
						);
						return;
					}
					const completedDraft =
						canCancelCorrection &&
						["published", "cancelled"].includes(data.status);
					const changedTranscript =
						recovering &&
						Boolean(sourceHash) &&
						data.sourceTranscript !== source?.formattedTranscript;
					setTextMarks(
						changedTranscript || completedDraft
							? []
							: Array.isArray(data.textMarks)
								? data.textMarks
								: [],
					);
					setAudioMarks(
						completedDraft
							? []
							: Array.isArray(data.audioMarks)
								? data.audioMarks
								: [],
					);
					setSavedMarks(
						completedDraft
							? JSON.stringify([[], []])
							: JSON.stringify([data.textMarks ?? [], data.audioMarks ?? []]),
					);
					setRevision(Number(data.revision ?? 0));
					setPreview(
						changedTranscript || completedDraft
							? null
							: data.preview?.status === "ready"
								? data.preview
								: null,
					);
					if (changedTranscript)
						setMessage(
							"The stored transcript changed during publication. Review it and mark sensitive content again before saving a new draft.",
						);
					setDraftStatus(
						typeof data.status === "string" ? data.status : "draft",
					);
					setPublicationIssue(data.publicationIssue ?? null);
				},
				() => setMessage("Could not recover the saved draft."),
			),
		[
			companyId,
			callSid,
			sourceHash,
			source?.piiProcessing?.attemptId,
			source?.formattedTranscript,
			canCancelCorrection,
		],
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: A new preview invalidates all reviewer attestations even when its content is otherwise equal.
	useEffect(() => {
		setReviewedText(false);
		setReviewedAudio(false);
		setManualOverride(false);
	}, [preview?.id]);
	useEffect(() => {
		if (!preview?.audioUri) {
			setPreviewAudioUrl(null);
			return;
		}
		let active = true;
		let loadedUrl: string | null = null;
		setPreviewAudioUrl(null);
		setPreviewAudioStatus("loading");
		void reviewAudioUrl(preview.audioUri)
			.then((url) => {
				loadedUrl = url;
				if (active) {
					setPreviewAudioUrl(url);
					setPreviewAudioStatus("ready");
				} else releaseReviewAudio(url);
			})
			.catch(() => {
				if (active) setPreviewAudioStatus("error");
			});
		return () => {
			active = false;
			releaseReviewAudio(loadedUrl);
		};
	}, [preview?.audioUri]);
	useEffect(() => {
		if (!sourceAudioUrl) {
			setPeaks([]);
			return;
		}
		let active = true;
		const controller = new AbortController();
		setPeaks([]);
		setWaveformError(false);
		void fetch(sourceAudioUrl, { signal: controller.signal })
			.then((response) => {
				if (!response.ok) throw new Error("Recording download failed.");
				return response.arrayBuffer();
			})
			.then(async (buffer) => {
				const context = new AudioContext();
				try {
					const decoded = await context.decodeAudioData(buffer);
					const channel = decoded.getChannelData(0);
					const step = Math.max(1, Math.floor(channel.length / 96));
					const values = Array.from({ length: 96 }, (_, index) => {
						let max = 0;
						for (
							let sample = index * step;
							sample < Math.min(channel.length, (index + 1) * step);
							sample++
						)
							max = Math.max(max, Math.abs(channel[sample]));
						return Math.max(0.08, max);
					});
					if (active) {
						setPeaks(values);
						setDuration(decoded.duration);
					}
				} finally {
					await context.close();
				}
			})
			.catch(() => {
				if (active) setWaveformError(true);
			});
		return () => {
			active = false;
			controller.abort();
		};
	}, [sourceAudioUrl]);
	const [addAttempted, setAddAttempted] = useState(false);
	const [rangeAttempted, setRangeAttempted] = useState(false);
	const [saveAttempted, setSaveAttempted] = useState(false);
	const [publishAttempted, setPublishAttempted] = useState(false);
	const [focusValidation, setFocusValidation] = useState(false);
	const editorRef = useRef<HTMLDivElement>(null);
	const addReasonError = markReasonError({ type: selectedType, reason });
	const fieldErrors = saveAttempted
		? reviewMarkErrors(transcript, textMarks, audioMarks, duration)
		: {};
	const rangeErrors = rangeAttempted
		? reviewMarkErrors(
				"",
				[],
				[{ startSec: rangeStart, endSec: rangeEnd, type: selectedType }],
				duration,
			)
		: {};
	const publicationError =
		publishAttempted && !publishReason.trim()
			? "Enter a reason for publishing this review."
			: undefined;
	useEffect(() => {
		if (!focusValidation) return;
		const field = editorRef.current?.querySelector<HTMLElement>(
			'[aria-invalid="true"]',
		);
		field?.focus();
		field?.scrollIntoView?.({ block: "center", behavior: "smooth" });
		setFocusValidation(false);
	}, [focusValidation]);
	const checkAddReason = () => {
		setAddAttempted(true);
		if (addReasonError) {
			setMessage("Correct the highlighted field before adding a mark.");
			setFocusValidation(true);
			return false;
		}
		return true;
	};
	const marksDirty = JSON.stringify([textMarks, audioMarks]) !== savedMarks;
	const addTextMark = () => {
		const area = transcriptRef.current;
		if (!checkAddReason()) return;
		if (!area || area.selectionEnd <= area.selectionStart) {
			setMessage("Select transcript text before adding a mark.");
			return;
		}
		setTextMarks((current) => [
			...current,
			{
				start: area.selectionStart,
				end: area.selectionEnd,
				type: selectedType,
				...(reason.trim() ? { reason: reason.trim() } : {}),
			},
		]);
		setMessage("Text mark added. Save the draft to keep it.");
	};
	const addAudioMark = () => {
		if (!checkAddReason()) return;
		setRangeAttempted(true);
		const errors = reviewMarkErrors(
			"",
			[],
			[{ startSec: rangeStart, endSec: rangeEnd, type: selectedType }],
			duration,
		);
		if (errors["audio-0-start"] || errors["audio-0-end"]) {
			setMessage("Correct the highlighted audio range before adding a mark.");
			setFocusValidation(true);
			return;
		}
		setAudioMarks((current) => [
			...current,
			{
				startSec: rangeStart,
				endSec: rangeEnd,
				type: selectedType,
				...(reason.trim() ? { reason: reason.trim() } : {}),
			},
		]);
		setMessage("Audio mark added. Save the draft to keep it.");
	};
	const addSuggestedText = (suggestion: Suggestion) => {
		if (!suggestion.text) return;
		const start = transcript.indexOf(suggestion.text);
		if (start < 0) {
			setMessage(
				"Suggested text is not present in this transcript. Select it manually.",
			);
			return;
		}
		const type = typeOptions.some(([value]) => value === suggestion.type)
			? (suggestion.type as MarkType)
			: "other_sensitive_content";
		setTextMarks((marks) => [
			...marks,
			{
				start,
				end: start + suggestion.text!.length,
				type,
				...(type === "other_sensitive_content"
					? { reason: "Automatic suggestion" }
					: {}),
			},
		]);
	};
	const addSuggestedAudio = (suggestion: Suggestion) => {
		if (
			typeof suggestion.audioStartSec !== "number" ||
			typeof suggestion.audioEndSec !== "number"
		)
			return;
		const type = typeOptions.some(([value]) => value === suggestion.type)
			? (suggestion.type as MarkType)
			: "other_sensitive_content";
		setAudioMarks((marks) =>
			marks.some(
				(mark) =>
					mark.type === type &&
					mark.startSec === suggestion.audioStartSec &&
					mark.endSec === suggestion.audioEndSec,
			)
				? marks
				: [
						...marks,
						{
							startSec: suggestion.audioStartSec!,
							endSec: suggestion.audioEndSec!,
							type,
							...(suggestion.audioRequiresHumanReview
								? {
										confidence: suggestion.audioMatchConfidence ?? 0.65,
										requiresReview: true,
										confirmed: false,
									}
								: {}),
							...(type === "other_sensitive_content"
								? { reason: "Automatic suggestion" }
								: {}),
						},
					],
		);
	};
	const saveDraft = async () => {
		setSaveAttempted(true);
		if (
			Object.keys(reviewMarkErrors(transcript, textMarks, audioMarks, duration))
				.length
		) {
			toast.error("Correct the highlighted fields before saving the draft.");
			setMessage("Correct the highlighted fields before saving the draft.");
			setFocusValidation(true);
			return;
		}
		if (textMarks.length + audioMarks.length > 100) {
			toast.error(
				"Keep the draft to 100 marks or fewer. Remove extra marks before saving.",
			);
			setMessage(
				"Keep the draft to 100 marks or fewer. Remove extra marks before saving.",
			);
			return;
		}
		setBusy(true);
		const toastId = toast.loading("Saving draft…");
		try {
			const save = httpsCallable<unknown, { revision: number }>(
				functions,
				"saveCallPiiReviewDraft",
			);
			const result = await save({
				companyId,
				callSid,
				textMarks,
				audioMarks,
				sourceHash,
				revision,
			});
			setRevision(result.data.revision);
			setSavedMarks(JSON.stringify([textMarks, audioMarks]));
			setPreview(null);
			setPublicationIssue(null);
			setManualOverride(false);
			setReviewedText(false);
			setReviewedAudio(false);
			setMessage(`Draft saved · revision ${result.data.revision}`);
			toast.success("Draft saved.", { id: toastId });
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Could not save draft.";
			setMessage(message);
			toast.error(message, { id: toastId });
		} finally {
			setBusy(false);
		}
	};
	const generatePreview = async () => {
		setBusy(true);
		const toastId = toast.loading("Generating preview…");
		try {
			const generate = httpsCallable<unknown, { previewId: string }>(
				functions,
				"generateCallPiiReviewPreview",
			);
			await generate({ companyId, callSid, revision });
			setReviewedText(false);
			setReviewedAudio(false);
			setManualOverride(false);
			setMessage(
				"Preview generated. Review the proposed transcript and recording before publishing.",
			);
			toast.success(
				"Preview generated. Review the text and recording before publishing.",
				{ id: toastId },
			);
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Could not generate preview.";
			setMessage(message);
			toast.error(message, { id: toastId });
		} finally {
			setBusy(false);
		}
	};
	const publish = async () => {
		setPublishAttempted(true);
		if (!publishReason.trim()) {
			toast.error("Enter a reason for publishing this review.");
			setMessage("Correct the highlighted field before publishing.");
			setFocusValidation(true);
			return;
		}
		setBusy(true);
		const toastId = toast.loading("Publishing reviewed result…");
		try {
			const call = httpsCallable<unknown, { success: boolean }>(
				functions,
				"publishCallPiiReview",
			);
			await call({
				companyId,
				callSid,
				revision,
				reason: publishReason.trim(),
				...(manualOverride
					? {
							manualOverride: true,
							reviewedText,
							reviewedAudio,
							previewId: preview?.id,
						}
					: {}),
			});
			setPublicationIssue(null);
			setDraftStatus("published");
			setPreview(null);
			setSource(null);
			setTextMarks([]);
			setAudioMarks([]);
			setCanCancelCorrection(false);
			setMessage("Reviewed transcript and recording published.");
			toast.success("Reviewed transcript and recording published.", {
				id: toastId,
			});
			onBack();
		} catch (error) {
			const details = (
				error as {
					details?: {
						kind?: string;
						canOverride?: boolean;
						issue?: { message: string; artifact: string; field: string };
					};
				}
			)?.details;
			if (
				details?.kind === "pii-publication-review" &&
				details.canOverride &&
				details.issue
			) {
				setPublicationIssue(details.issue);
				setManualOverride(false);
				setDraftStatus("publication_failed");
			}
			const message =
				details?.issue?.message ??
				(error instanceof Error
					? error.message
					: "Could not publish the review.");
			setMessage(details?.kind === "pii-publication-review" ? "" : message);
			toast.error(message, { id: toastId });
		} finally {
			setBusy(false);
		}
	};
	const cancelCorrection = async () => {
		setBusy(true);
		const toastId = toast.loading("Cancelling correction…");
		try {
			await httpsCallable(
				functions,
				"cancelCallPiiCorrection",
			)({
				companyId,
				callSid,
				reason: "Admin cancelled correction and kept the published result",
			});
			setPreview(null);
			setSource(null);
			setTextMarks([]);
			setAudioMarks([]);
			setCanCancelCorrection(false);
			toast.success("Correction cancelled. The published result was kept.", {
				id: toastId,
			});
			onBack();
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "Could not cancel the correction. The call remains open.";
			setMessage(message);
			toast.error(message, { id: toastId });
		} finally {
			setBusy(false);
		}
	};
	return (
		<div ref={editorRef} className="space-y-5">
			<div className="flex flex-wrap items-center gap-2">
				<Button variant="ghost" disabled={busy} onClick={onBack}>
					<ArrowLeft className="mr-2 size-4" />
					Back to case
				</Button>
				{canCancelCorrection && (
					<Button
						variant="outline"
						disabled={busy}
						onClick={() => void cancelCorrection()}
					>
						Cancel correction — keep published result
					</Button>
				)}
			</div>
			<header>
				<p className="text-xs uppercase tracking-widest text-muted-foreground">
					PII review / Editor
				</p>
				<h1 className="break-all text-3xl font-semibold">{callSid}</h1>
				<p className="mt-2 text-sm text-muted-foreground">
					{editingCandidateId
						? `Reviewing candidate ${editingCandidateId}: original recording and transcript. Changes take effect when you publish.`
						: "Reviewing the stored call recording and transcript."}
				</p>
				<p className="mt-1 text-sm text-muted-foreground">
					Select transcript text or set an audio range, then save the draft.
				</p>
			</header>
			{message && (
				<p role="status" className="rounded-md border bg-white p-3 text-sm">
					{message}
				</p>
			)}
			<div className="grid gap-4 xl:grid-cols-2">
				<section
					aria-labelledby="source-transcript-heading"
					className="rounded-lg border bg-white p-5"
				>
					<h2 id="source-transcript-heading" className="text-sm font-semibold">
						Source transcript
					</h2>
					<p className="mt-1 text-xs text-muted-foreground">
						Use Shift and arrow keys to select text, then choose Add text mark.
					</p>
					{sourceStatus === "loading" ? (
						<EvidenceLoading label="Loading source transcript…" />
					) : sourceStatus === "error" ? (
						<EvidenceError
							message={
								candidateId && sourceError
									? sourceError
									: "Could not load the source transcript. Try loading the source again."
							}
							onRetry={() => setLoadAttempt((value) => value + 1)}
						/>
					) : transcript ? (
						<MarkedTranscript
							text={transcript}
							marks={textMarks}
							textareaRef={transcriptRef}
						/>
					) : (
						<p className="mt-4 text-sm text-muted-foreground">
							This call has no source transcript.
						</p>
					)}
					<Button
						className="mt-3"
						variant="outline"
						onClick={addTextMark}
						disabled={sourceStatus !== "ready" || !transcript}
					>
						Add text mark
					</Button>
				</section>
				<section
					aria-labelledby="source-audio-heading"
					className="rounded-lg border bg-white p-5"
				>
					<h2 id="source-audio-heading" className="text-sm font-semibold">
						Source recording
					</h2>
					<p className="mt-1 text-xs text-muted-foreground">
						Click the waveform to choose a start point; Shift-click to extend
						the span. Use arrow keys or enter exact seconds to adjust it.
					</p>
					{audioStatus === "loading" ? (
						<EvidenceLoading label="Loading source recording…" />
					) : audioStatus === "ready" && sourceAudioUrl ? (
						<>
							<MarkedRecording
								url={sourceAudioUrl}
								peaks={peaks}
								duration={duration}
								selection={{ startSec: rangeStart, endSec: rangeEnd }}
								marks={audioMarks}
								audioRef={audioRef}
								waveformError={waveformError}
								onPlaybackError={() => setAudioStatus("error")}
								onDuration={(value) => setDuration(value)}
								onPickTime={(seconds, extend) => {
									if (extend) {
										setRangeStart(Math.min(rangeStart, seconds));
										setRangeEnd(Math.max(rangeStart, seconds));
									} else {
										setRangeStart(seconds);
										setRangeEnd(seconds);
									}
									setRangeAttempted(false);
								}}
							/>
							<div className="mt-4 grid grid-cols-2 gap-3">
								<label className="text-xs">
									Start (seconds)
									<ReviewInput
										label="Start (seconds)"
										error={rangeErrors["audio-0-start"]}
										type="number"
										min="0"
										max={duration}
										step="0.01"
										value={rangeStart}
										onChange={(event) =>
											setRangeStart(Number(event.target.value))
										}
									/>
								</label>
								<label className="text-xs">
									End (seconds)
									<ReviewInput
										label="End (seconds)"
										error={rangeErrors["audio-0-end"]}
										type="number"
										min="0"
										max={duration}
										step="0.01"
										value={rangeEnd}
										onChange={(event) =>
											setRangeEnd(Number(event.target.value))
										}
									/>
								</label>
							</div>
							<label className="mt-3 block text-xs">
								Drag start
								<input
									className="w-full accent-teal-700"
									type="range"
									min="0"
									max={duration}
									step="0.01"
									value={rangeStart}
									onChange={(event) =>
										setRangeStart(Number(event.target.value))
									}
								/>
							</label>
							<label className="mt-2 block text-xs">
								Drag end
								<input
									className="w-full accent-teal-700"
									type="range"
									min="0"
									max={duration}
									step="0.01"
									value={rangeEnd}
									onChange={(event) => setRangeEnd(Number(event.target.value))}
								/>
							</label>
							<div className="mt-3 flex gap-2">
								<Button
									variant="outline"
									onClick={() => {
										if (audioRef.current) {
											audioRef.current.currentTime = rangeStart;
											void audioRef.current.play();
										}
									}}
								>
									Play from start
								</Button>
								<Button variant="outline" onClick={addAudioMark}>
									Add audio mark
								</Button>
							</div>
						</>
					) : (
						<EvidenceError
							message={
								audioStatus === "missing"
									? "This call has no source recording. Audio review is blocked."
									: "Could not load the source recording. Try loading the source again."
							}
							onRetry={
								audioStatus === "missing"
									? undefined
									: () => setLoadAttempt((value) => value + 1)
							}
						/>
					)}
				</section>
			</div>
			{!editingCandidateId && suggestions.length > 0 && (
				<section
					aria-label="Suggested matches"
					className="rounded-lg border bg-white p-5"
				>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<h2 className="text-sm font-semibold">Suggested matches</h2>
						<Button
							size="sm"
							variant="outline"
							onClick={() => suggestions.forEach(addSuggestedAudio)}
						>
							Add suggested audio ranges
						</Button>
					</div>
					<p className="mt-1 text-xs text-muted-foreground">
						Add text and audio independently, then adjust or remove each mark.
					</p>
					<ul className="mt-3 space-y-2">
						{suggestions.map((suggestion, index) => (
							<li
								key={index}
								className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
							>
								<span>
									{suggestion.type.replaceAll("_", " ")}
									{typeof suggestion.audioMatchConfidence === "number"
										? ` · Audio confidence ${Math.round(suggestion.audioMatchConfidence * 100)}%`
										: ""}
									{suggestion.audioRequiresHumanReview
										? " · Needs audio review"
										: ""}
									{suggestion.text ? ` · ${suggestion.text}` : ""}
								</span>
								<div className="flex gap-2">
									<Button
										size="sm"
										variant="outline"
										disabled={!suggestion.text}
										onClick={() => addSuggestedText(suggestion)}
									>
										Add text
									</Button>
									<Button
										size="sm"
										variant="outline"
										disabled={
											typeof suggestion.audioStartSec !== "number" ||
											typeof suggestion.audioEndSec !== "number"
										}
										onClick={() => addSuggestedAudio(suggestion)}
									>
										Add audio
									</Button>
								</div>
							</li>
						))}
					</ul>
				</section>
			)}
			<section
				aria-labelledby="marks-heading"
				className="rounded-lg border bg-white p-5"
			>
				<h2 id="marks-heading" className="text-sm font-semibold">
					Marks · {textMarks.length} text / {audioMarks.length} audio
				</h2>
				<div className="mt-3 grid gap-3 sm:grid-cols-2">
					<label className="text-xs">
						PII type
						<select
							className="mt-1 w-full rounded-md border bg-white p-2 text-sm"
							value={selectedType}
							onChange={(event) =>
								setSelectedType(event.target.value as MarkType)
							}
						>
							{typeOptions.map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</select>
					</label>
					{selectedType === "other_sensitive_content" && (
						<label className="text-xs">
							Reason required
							<ReviewInput
								label="Reason required"
								error={addAttempted ? addReasonError : undefined}
								className="mt-1"
								value={reason}
								maxLength={500}
								onChange={(event) => setReason(event.target.value)}
							/>
						</label>
					)}
				</div>
				<ul className="mt-4 space-y-2 text-sm">
					{textMarks.map((mark, index) => (
						<li
							key={`text-${index}`}
							className="grid gap-2 rounded-md border p-3 sm:grid-cols-[1fr_1fr_1.5fr_auto]"
						>
							<label className="text-xs">
								Text start
								<ReviewInput
									label="Text start"
									error={fieldErrors[`text-${index}-start`]}
									type="number"
									min="0"
									max={transcript.length}
									value={mark.start}
									onChange={(event) =>
										setTextMarks((marks) =>
											marks.map((item, position) =>
												position === index
													? { ...item, start: Number(event.target.value) }
													: item,
											),
										)
									}
								/>
							</label>
							<label className="text-xs">
								Text end
								<ReviewInput
									label="Text end"
									error={fieldErrors[`text-${index}-end`]}
									type="number"
									min="0"
									max={transcript.length}
									value={mark.end}
									onChange={(event) =>
										setTextMarks((marks) =>
											marks.map((item, position) =>
												position === index
													? { ...item, end: Number(event.target.value) }
													: item,
											),
										)
									}
								/>
							</label>
							<label className="text-xs">
								Type
								<select
									className="w-full rounded-md border bg-white p-2 text-sm"
									value={mark.type}
									onChange={(event) =>
										setTextMarks((marks) =>
											marks.map((item, position) =>
												position === index
													? { ...item, type: event.target.value as MarkType }
													: item,
											),
										)
									}
								>
									{typeOptions.map(([value, label]) => (
										<option key={value} value={value}>
											{label}
										</option>
									))}
								</select>
							</label>
							<Button
								size="sm"
								variant="outline"
								className="self-end"
								onClick={() =>
									setTextMarks((marks) =>
										marks.filter((_, item) => item !== index),
									)
								}
							>
								Remove
							</Button>
							{mark.type === "other_sensitive_content" && (
								<label className="text-xs sm:col-span-4">
									Reason
									<ReviewInput
										label="Reason"
										error={fieldErrors[`text-${index}-reason`]}
										value={mark.reason ?? ""}
										maxLength={500}
										onChange={(event) =>
											setTextMarks((marks) =>
												marks.map((item, position) =>
													position === index
														? { ...item, reason: event.target.value }
														: item,
												),
											)
										}
									/>
								</label>
							)}
						</li>
					))}
					{audioMarks.map((mark, index) => (
						<li
							key={`audio-${index}`}
							className="grid gap-2 rounded-md border p-3 sm:grid-cols-[1fr_1fr_1.5fr_auto]"
						>
							<label className="text-xs">
								Audio start (s)
								<ReviewInput
									label="Audio start (s)"
									error={fieldErrors[`audio-${index}-start`]}
									type="number"
									min="0"
									max={duration}
									step="0.01"
									value={mark.startSec}
									onChange={(event) =>
										setAudioMarks((marks) =>
											marks.map((item, position) =>
												position === index
													? { ...item, startSec: Number(event.target.value) }
													: item,
											),
										)
									}
								/>
							</label>
							<label className="text-xs">
								Audio end (s)
								<ReviewInput
									label="Audio end (s)"
									error={fieldErrors[`audio-${index}-end`]}
									type="number"
									min="0"
									max={duration}
									step="0.01"
									value={mark.endSec}
									onChange={(event) =>
										setAudioMarks((marks) =>
											marks.map((item, position) =>
												position === index
													? { ...item, endSec: Number(event.target.value) }
													: item,
											),
										)
									}
								/>
							</label>
							<label className="text-xs">
								Type
								<select
									className="w-full rounded-md border bg-white p-2 text-sm"
									value={mark.type}
									onChange={(event) =>
										setAudioMarks((marks) =>
											marks.map((item, position) =>
												position === index
													? { ...item, type: event.target.value as MarkType }
													: item,
											),
										)
									}
								>
									{typeOptions.map(([value, label]) => (
										<option key={value} value={value}>
											{label}
										</option>
									))}
								</select>
							</label>
							<Button
								size="sm"
								variant="outline"
								className="self-end"
								onClick={() =>
									setAudioMarks((marks) =>
										marks.filter((_, item) => item !== index),
									)
								}
							>
								Remove
							</Button>
							{mark.requiresReview && (
								<label className="flex items-center gap-2 text-sm sm:col-span-4">
									<input
										type="checkbox"
										checked={mark.confirmed === true}
										onChange={(event) =>
											setAudioMarks((marks) =>
												marks.map((item, position) =>
													position === index
														? { ...item, confirmed: event.target.checked }
														: item,
												),
											)
										}
									/>
									I checked this approximate range (
									{Math.round((mark.confidence ?? 0.65) * 100)}% confidence)
									against the recording
								</label>
							)}
							{mark.type === "other_sensitive_content" && (
								<label className="text-xs sm:col-span-4">
									Reason
									<ReviewInput
										label="Reason"
										error={fieldErrors[`audio-${index}-reason`]}
										value={mark.reason ?? ""}
										maxLength={500}
										onChange={(event) =>
											setAudioMarks((marks) =>
												marks.map((item, position) =>
													position === index
														? { ...item, reason: event.target.value }
														: item,
												),
											)
										}
									/>
								</label>
							)}
						</li>
					))}
				</ul>
			</section>
			<section
				aria-label="Review sequence"
				className="rounded-lg border bg-white p-5"
			>
				<p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
					Save draft → Generate preview → Review text and audio → Publish
				</p>
				<div className="flex flex-wrap gap-2">
					<Button
						className="bg-teal-700 text-white hover:bg-teal-800"
						onClick={() => void saveDraft()}
						disabled={
							busy ||
							sourceStatus !== "ready" ||
							!sourceHash ||
							(draftStatus === "published" && !canCancelCorrection)
						}
					>
						Save draft
					</Button>
					<Button
						variant="outline"
						onClick={() => void generatePreview()}
						disabled={
							busy ||
							sourceStatus !== "ready" ||
							marksDirty ||
							revision < 1 ||
							draftStatus !== "draft"
						}
					>
						Generate preview
					</Button>
				</div>
				{preview && (
					<div className="mt-5 grid gap-4 lg:grid-cols-2">
						<div className="rounded-md border p-4">
							<h3 className="text-sm font-semibold">Proposed transcript</h3>
							<pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-words font-sans text-sm">
								{preview.proposedTranscript}
							</pre>
							<label className="mt-3 flex items-center gap-2 text-sm">
								<input
									type="checkbox"
									checked={reviewedText}
									onChange={(event) => setReviewedText(event.target.checked)}
								/>
								I reviewed the proposed text
							</label>
						</div>
						<div className="rounded-md border p-4">
							<h3 className="text-sm font-semibold">
								Redacted recording preview
							</h3>
							{previewAudioStatus === "loading" ? (
								<EvidenceLoading label="Loading preview recording…" />
							) : previewAudioUrl ? (
								<audio
									controls
									onError={() => {
										setPreviewAudioUrl(null);
										setPreviewAudioStatus("error");
										setReviewedAudio(false);
										setManualOverride(false);
									}}
									src={previewAudioUrl}
									className="mt-3 w-full"
								>
									<track kind="captions" />
								</audio>
							) : (
								<p role="alert" className="mt-3 text-sm text-red-700">
									Could not load the preview recording. Reload the page to try
									again.
								</p>
							)}
							<label className="mt-3 flex items-center gap-2 text-sm">
								<input
									type="checkbox"
									checked={reviewedAudio}
									onChange={(event) => setReviewedAudio(event.target.checked)}
									disabled={!previewAudioUrl}
								/>
								I listened to the proposed audio
							</label>
						</div>
					</div>
				)}
				{publicationIssue && (
					<div
						role="alert"
						className="mt-4 space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
					>
						<p className="font-semibold">Publication needs your review</p>
						<p>{publicationIssue.message}</p>
						<p>
							Check: {publicationIssue.artifact.split("/").at(-1)} ·{" "}
							{publicationIssue.field}
						</p>
						<p>
							You have the final decision. Correct the marks and generate a new
							preview, or confirm the full review below and override this
							automated finding. Your reason and overridden findings will be
							recorded.
						</p>
						<Button
							variant="outline"
							disabled={busy}
							onClick={() => {
								setManualOverride(false);
								setReviewedText(false);
								setReviewedAudio(false);
								setLoadAttempt((value) => value + 1);
								transcriptRef.current?.focus();
								transcriptRef.current?.scrollIntoView?.({ block: "center" });
							}}
						>
							Return to editing
						</Button>
					</div>
				)}
				{preview && (
					<div className="mt-4 space-y-3">
						{preview.publicationBlocked && (
							<div role="status" className="text-sm text-amber-800">
								<p>
									Preview available. Publication is blocked until these items
									are resolved:
								</p>
								<ul className="list-disc pl-5">
									{preview.publicationBlockers?.map((reason) => (
										<li key={reason}>{reason}</li>
									))}
								</ul>
							</div>
						)}
						{(preview.publicationBlocked || publicationIssue) && (
							<label className="flex items-start gap-2 text-sm">
								<input
									type="checkbox"
									checked={manualOverride}
									disabled={!reviewedText || !reviewedAudio || marksDirty}
									onChange={(event) => setManualOverride(event.target.checked)}
								/>
								I reviewed the entire transcript and recording, marked all
								sensitive content, and override the automated PII errors.
							</label>
						)}
						<label className="block text-xs">
							Publication reason
							<ReviewInput
								label="Publication reason"
								error={publicationError}
								value={publishReason}
								maxLength={1000}
								onChange={(event) => setPublishReason(event.target.value)}
								className="mt-1"
							/>
						</label>
						<Button
							className="bg-teal-700 text-white hover:bg-teal-800"
							disabled={
								busy ||
								marksDirty ||
								((preview.publicationBlocked === true ||
									Boolean(publicationIssue)) &&
									!manualOverride) ||
								!reviewedText ||
								!reviewedAudio ||
								draftStatus === "published"
							}
							onClick={() => void publish()}
						>
							{publicationIssue && manualOverride
								? "Override and publish reviewed result"
								: "Publish reviewed result"}
						</Button>
					</div>
				)}
			</section>
		</div>
	);
}
